import { useCallback } from "react";
import { createDocumentResource } from "./document-resource";
import { openDocumentResource } from "@/stores/document-preview-store";
import { useApolloClient } from "@apollo/client/react";
import { useIntl } from "react-intl";
import { DocumentContentDocument, OrganizationalRecordDocumentContentDocument } from "@/graphql/generated/operations";
import { userErrorMessage } from "@/lib/user-error";

import { base64ToBlob } from "./file-bytes";
export { base64ToBlob } from "./file-bytes";

export function useOpenDocument(onError: (message: string) => void) {
  const intl = useIntl();
  const apollo = useApolloClient();
  return async function openDocument(id: string) {
    try {
      const { data } = await apollo.query({ query: DocumentContentDocument, variables: { id }, fetchPolicy: "no-cache" });
      const file = data?.document?.content;
      if (!file?.content) throw new Error("Missing document content");
      const resource = createDocumentResource({ name: data?.document?.fileName ?? "document", mimeType: file.mimeType,
        load: async () => base64ToBlob(file.content!, file.mimeType ?? "application/octet-stream") });
      await openDocumentResource(resource);
    } catch (error) {
      onError(userErrorMessage(intl, error));
    }
  };
}

export function useDocumentResourceFactory(scope: "personal" | "organizational" = "personal") {
  const apollo = useApolloClient();
  return useCallback((id: string, name: string) => createDocumentResource({ name, load: async () => {
    const file = scope === "organizational"
      ? (await apollo.query({ query: OrganizationalRecordDocumentContentDocument, variables: { id }, fetchPolicy: "no-cache" })).data?.organizationalRecordDocument?.content
      : (await apollo.query({ query: DocumentContentDocument, variables: { id }, fetchPolicy: "no-cache" })).data?.document?.content;
    if (!file?.content) throw new Error("Missing document content");
    return base64ToBlob(file.content, file.mimeType ?? "application/octet-stream");
  } }), [apollo, scope]);
}
